import { Badge, Divider, List, Stack } from '@mantine/core';
import { useThrottledValue } from '@mantine/hooks';
import { IconListCheck } from '@tabler/icons-react';

import type { PlanInput, PlanOutput } from './schema.ts';
import { ToolDisplay } from '#plugin-chat/components/tool-display/ToolDisplay.tsx';
import type { ToolProgressComponent } from '#plugin-chat/lib/tool/progress-registry.ts';

/**
 * UI component for PlanTool — renders the plan as a numbered checklist with per-step
 * tool chips, plus the final-grain / expected-output header strip and an optional
 * assumptions block.
 */
export function RenderPlanTool({ args }: { args: PlanInput }) {
  const { summary, steps, assumptions, finalGrain, expectedOutput } = useThrottledValue(args, 200);

  return (
    <ToolDisplay>
      <ToolDisplay.Running>Planning...</ToolDisplay.Running>
      <ToolDisplay.Complete icon={IconListCheck}>Execution plan created</ToolDisplay.Complete>
      <ToolDisplay.Content>
        <Stack gap="sm">
          {summary && (
            <div>
              <strong>{summary}</strong>
            </div>
          )}
          {!!steps?.length && (
            <div>
              <Divider label="Steps" labelPosition="left" />
              <List type="ordered">
                {steps.map((step, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: Plan steps render in a fixed order; index is stable.
                  <List.Item key={idx}>
                    {step?.description}
                    {step?.tool && (
                      <Badge ml="xs" size="sm" variant="light">
                        {step.tool}
                      </Badge>
                    )}
                  </List.Item>
                ))}
              </List>
            </div>
          )}

          {!!assumptions?.length && (
            <div>
              <Divider label="Assumptions" labelPosition="left" />
              <List type="unordered">
                {assumptions.map((assumption, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: Plan assumptions render in a fixed order; index is stable.
                  <List.Item key={idx}>{assumption}</List.Item>
                ))}
              </List>
            </div>
          )}

          {finalGrain && <div>Final grain: {finalGrain}</div>}

          {expectedOutput && <div>Expected output: {expectedOutput}</div>}
        </Stack>
      </ToolDisplay.Content>
    </ToolDisplay>
  );
}
/**
 * Progress component for PlanTool.
 *
 * Rendered inside the "AiPSO Agent is ..." progress bubble while the tool is running.
 */
export const ProgressPlanTool: ToolProgressComponent<Partial<PlanInput>, PlanOutput> = () => <>planning...</>;
